// ActivityStore 选择器 hooks（§5.7.1 单一事实源）
import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { isActivityShowable, useActivityStore } from './slice';

import type { IActivity, TActivityPopupType, TActivityStatus } from '@/activity-orchestration/types';

/** 按条件过滤活动（业务页 / 角标等，不绑挂件可见性） */
export interface IActivityFilter {
    /** 精确匹配活动 id */
    activityId?: string;
    /** 匹配单个或多个 type */
    type?: TActivityPopupType | TActivityPopupType[];
    /** 匹配 status；与 showableOnly 可叠加 */
    status?: TActivityStatus;
    /**
     * 是否仅返回 isActivityShowable 为 true 的活动。
     * 默认 true；为 false 时不做过期 / status 综合校验（仍可单独传 status）。
     */
    showableOnly?: boolean;
}

/**
 * 从活动 map 中按过滤条件取一条（多条时 priority 大者优先，同优先取更早 createdAt）。
 * @param activities ActivityStore.activities
 * @param filter 过滤条件
 * @param now 当前时间戳（可测）
 * @returns 命中的活动；无命中为 undefined
 */
export function selectMatchedActivity(
    activities: Record<string, IActivity>,
    filter: IActivityFilter,
    now: number = Date.now()
): IActivity | undefined {
    const { activityId, type, status, showableOnly = true } = filter;
    let types: TActivityPopupType[] | null = null;
    if (type != null) {
        types = Array.isArray(type) ? type : [type];
    }

    const matched = Object.values(activities).filter(activity => {
        if (activityId != null && activity.activityId !== activityId) {
            return false;
        }
        if (types != null && !types.includes(activity.type)) {
            return false;
        }
        if (status != null && activity.status !== status) {
            return false;
        }
        if (showableOnly && !isActivityShowable(activity, now)) {
            return false;
        }
        return true;
    });

    if (matched.length === 0) {
        return undefined;
    }

    matched.sort(
        (a, b) =>
            b.priority - a.priority || // 优先级：大者先
            a.createdAt - b.createdAt || // 时间：早者先
            a.activityId.localeCompare(b.activityId)
    );
    return matched[0];
}

/** 按 activityId 订阅单个活动（渲染弹窗 / 挂件时取 payload） */
export const useActivity = (activityId: string) => useActivityStore(state => state.activities[activityId]);

/** 全量活动 map（可见性校时 / 调试用，谨慎订阅） */
export const useActivities = () => useActivityStore(state => state.activities);

/**
 * 按 activityId / type / status 等条件过滤并订阅一条活动。
 * 仅做 Store 过滤，倒计时展示逻辑由页面侧自行判断（expireAt + useCountdown）。
 * @param filter 过滤条件
 * @returns 命中的活动；无命中为 undefined
 */
export function useActivityMatch(filter: IActivityFilter): IActivity | undefined {
    const { activityId, type, status, showableOnly = true } = filter;
    const activities = useActivityStore(state => state.activities);
    let typeKey = '';
    if (type != null) {
        typeKey = Array.isArray(type) ? type.join(',') : type;
    }

    return useMemo(
        () => selectMatchedActivity(activities, { activityId, type, status, showableOnly }),
        // type 用 typeKey 稳定依赖；activities 引用随 store 更新
        // eslint-disable-next-line react-hooks/exhaustive-deps -- type 以 typeKey 代替引用比较
        [activities, activityId, typeKey, status, showableOnly]
    );
}

/** ActivityStore 动作集合 */
export const useActivityActions = () =>
    useActivityStore(
        useShallow(state => ({
            upsertActivity: state.upsertActivity,
            getActivity: state.getActivity,
            expireActivity: state.expireActivity,
            removeActivity: state.removeActivity,
            reset: state.reset,
        }))
    );
